import { useState } from "react";
import { HiChevronDown, HiChevronUp, HiX } from "react-icons/hi";
import { ContextualSnippet } from "./ContextualSnippet";
import { SignalLifecycleActions } from "./SignalLifecycleActions";
import { SignalProgressRing } from "./SignalProgressRing";
import {
  REJECT_REASON_LABELS,
  formatActor,
  formatDate,
  formatRelative,
} from "./signalLifecycle";
import {
  HiArrowTopRightOnSquare,
  HiArrowTrendingUp,
  HiCheckCircle,
  HiClock,
  HiExclamationTriangle,
  HiFire,
  HiInformationCircle,
  HiEnvelope,
  HiPhone,
  HiChartBar,
  HiWrenchScrewdriver,
  HiDocumentText,
} from "react-icons/hi2";
import { ActionItem } from "./ActionItem";
import { useAnalytics } from "../../../hooks/useAnalytics";

const IMPACT_CONFIG = {
  critical: {
    label: "Critical",
    className: "bg-red-100 text-red-800",
    Icon: HiFire,
  },
  high: {
    label: "High",
    className: "bg-red-50 text-red-700",
    Icon: HiFire,
  },
  medium: {
    label: "Med",
    className: "bg-amber-50 text-amber-700",
    Icon: HiExclamationTriangle,
  },
  low: {
    label: "Low",
    className: "bg-blue-50 text-blue-700",
    Icon: HiInformationCircle,
  },
  opportunity: {
    label: "Opportunity",
    className: "bg-emerald-50 text-emerald-700",
    Icon: HiArrowTrendingUp,
  },
};

const CONFIDENCE_COLOR = {
  High: "text-emerald-600",
  Med: "text-amber-600",
  Low: "text-gray-500",
};

const SOURCE_CONFIG = {
  usage: { Icon: HiChartBar, color: "text-blue-500", label: "Product usage" },
  email: { Icon: HiEnvelope, color: "text-violet-500", label: "Email/Comms" },
  call: { Icon: HiPhone, color: "text-purple-500", label: "Calls" },
  crm: { Icon: HiDocumentText, color: "text-emerald-500", label: "CRM" },
  support: {
    Icon: HiWrenchScrewdriver,
    color: "text-orange-500",
    label: "Support tickets",
  },
};

const groupEvidenceBySource = (evidence) => {
  const groups = {};
  const order = [];
  evidence.forEach((ev) => {
    if (!groups[ev.sourceType]) {
      groups[ev.sourceType] = [];
      order.push(ev.sourceType);
    }
    groups[ev.sourceType].push(ev);
  });
  return order.map((type) => ({ type, items: groups[type] }));
};

const EvidenceCard = ({ ev }) => {
  const [expanded, setExpanded] = useState(!!ev.detailContext);
  const config = SOURCE_CONFIG[ev.sourceType] ?? SOURCE_CONFIG.usage;
  const { Icon, color } = config;
  const hasExpandableDetail = ev.detail && ev.detail !== ev.headline;

  return (
    <div className="rounded-md border border-gray-200 bg-gray-50/50">
      <div className="px-3 py-2">
        <div className="flex items-start gap-2">
          <Icon className={`mt-0.5 size-3.5 shrink-0 ${color}`} />
          <div className="min-w-0 flex-1">
            <div className="mb-0.5 flex items-center gap-2">
              <span className="text-xs font-semibold text-gray-900">
                {ev.sourceName}
              </span>
              <span className="text-[10px] text-gray-400">{ev.timestamp}</span>
            </div>
            <div className="flex items-center gap-1">
              <p className="text-xs font-medium text-gray-800">{ev.headline}</p>
              {ev.sourceUrl && (
                <a
                  href={ev.sourceUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  className="shrink-0 text-gray-400 transition-colors hover:text-blue-500"
                  title="Open in source system"
                >
                  <HiArrowTopRightOnSquare className="size-3" />
                </a>
              )}
            </div>
            {ev.fromEmail && (
              <p className="mt-0.5 text-[10px] text-gray-400">
                From: {ev.fromEmail}
                {ev.toEmails?.length > 0 &&
                  ` → ${Array.isArray(ev.toEmails) ? ev.toEmails.join(", ") : ev.toEmails}`}
              </p>
            )}
            {ev.subline && (
              <p className="mt-0.5 text-[11px] leading-relaxed text-gray-500">
                {ev.subline}
              </p>
            )}
            {ev.citation && (
              <p className="mt-1 text-[10px] text-gray-300">{ev.citation}</p>
            )}
          </div>
          {hasExpandableDetail && (
            <button
              onClick={() => setExpanded((v) => !v)}
              className="shrink-0 rounded-md p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
            >
              {expanded ? (
                <HiChevronUp className="size-3.5" />
              ) : (
                <HiChevronDown className="size-3.5" />
              )}
            </button>
          )}
        </div>
      </div>
      {expanded && hasExpandableDetail && (
        <div className="border-t border-gray-100 bg-gray-100/60 px-3 py-2">
          <ContextualSnippet snippet={ev.detail} context={ev.detailContext} />
          {ev.citation && (
            <p className="mt-1 text-[10px] text-gray-300">{ev.citation}</p>
          )}
        </div>
      )}
    </div>
  );
};

/**
 * Caption describing why a signal left the active queue.
 * Shown on resolved / snoozed / rejected rows.
 */
const LifecycleCaption = ({ signal }) => {
  const actor = formatActor(signal.lifecycleBy);
  const when = formatRelative(signal.lifecycleAt);
  const suffix = `${actor}${when ? ` · ${when}` : ""}`;

  if (signal.status === "snoozed") {
    return (
      <span className="inline-flex items-center gap-1 text-[11px] text-gray-500">
        <HiClock className="size-3" />
        Snoozed
        {signal.snoozedUntil ? ` until ${formatDate(signal.snoozedUntil)}` : ""}
        {` · ${suffix}`}
      </span>
    );
  }
  if (signal.status === "resolved") {
    if (signal.resolvedAutomatically) {
      return (
        <span className="inline-flex items-center gap-1 text-[11px] text-emerald-600">
          <HiCheckCircle className="size-3" />
          Resolved automatically{when ? ` · ${when}` : ""}
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 text-[11px] text-emerald-600">
        <HiCheckCircle className="size-3" />
        Resolved · {suffix}
      </span>
    );
  }
  if (signal.status === "rejected") {
    const reason = signal.rejectReason
      ? (REJECT_REASON_LABELS[signal.rejectReason] ?? signal.rejectReason)
      : null;
    return (
      <span className="text-[11px] text-gray-400">
        Rejected{reason ? `: ${reason}` : ""} · {suffix}
      </span>
    );
  }
  return null;
};

export const SignalRow = ({
  signal,
  isExpanded,
  onToggle,
  onStatusChange,
  onNoteChange,
  onResolve,
  onSnooze,
  onReject,
  onReactivate,
}) => {
  const [activeTab, setActiveTab] = useState("actions");
  const { trackEvent } = useAnalytics();
  const isActive = signal.status === "active";

  const handleTabChange = (key) => {
    if (key === "evidence" && activeTab !== "evidence") {
      trackEvent("signal_evidence_viewed", {
        alert_id: signal.id,
        alert_type: signal.alertType ?? null,
      });
      const hasEvidence =
        signal.evidence?.sourceEvidence?.length ||
        signal.evidence?.metricEvidence?.length;
      if (!hasEvidence) {
        trackEvent("empty_state_shown", {
          surface: "signal_evidence",
          alert_id: signal.id,
          alert_type: signal.alertType ?? null,
        });
      }
    }
    setActiveTab(key);
  };

  const impactConfig = IMPACT_CONFIG[signal.impact] ?? IMPACT_CONFIG.low;
  const totalActions = signal.actions.length;
  const completedActions = signal.actions.filter(
    (a) => a.status === "completed" || a.status === "wont_do",
  ).length;

  const handleRowClick = (e) => {
    // Don't toggle when interacting with controls inside the row.
    if (
      e.target.closest("button") ||
      e.target.closest("select") ||
      e.target.closest("textarea") ||
      e.target.closest("a") ||
      e.target.closest("[data-testid$='-menu']")
    )
      return;
    onToggle();
  };

  const openActionCount = totalActions - completedActions;

  const lifecycleProps = {
    status: signal.status,
    openActionCount,
    onResolve: () => onResolve?.(signal.id),
    onSnooze: (until) => onSnooze?.(signal.id, until),
    onReject: (reason, note) => onReject?.(signal.id, reason, note),
    onReactivate: () => onReactivate?.(signal.id),
  };

  const tabs = [
    {
      key: "actions",
      label: "Actions",
      count: `${completedActions}/${totalActions}`,
    },
    {
      key: "evidence",
      label: "Evidence",
      count:
        (signal.evidence?.metricEvidence?.length ?? 0) +
        (signal.evidence?.sourceEvidence?.length ?? 0),
    },
  ];

  return (
    <div
      className={`rounded-lg border bg-white transition-all ${
        isExpanded
          ? "border-blue-200 shadow-sm ring-1 ring-blue-100"
          : "border-gray-200 hover:border-gray-300"
      }`}
    >
      {/* Header row */}
      <div
        className="flex cursor-pointer items-center gap-3 px-4 py-3"
        onClick={handleRowClick}
      >
        {/* Impact badge */}
        <span
          className={`inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-md px-2 py-1 text-[11px] font-semibold ${impactConfig.className}`}
        >
          <impactConfig.Icon className="size-3" />
          {impactConfig.label}
        </span>

        {/* Title + description */}
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <h3 className="truncate text-sm font-semibold text-gray-900">
              {signal.title}
            </h3>
            <span className="shrink-0 whitespace-nowrap text-xs text-gray-400">
              {signal.timeAgo}
            </span>
          </div>
        </div>

        {/* Progress */}
        <SignalProgressRing completed={completedActions} total={totalActions} />

        {/* Divider — only when the right slot has a control */}
        {(isExpanded || isActive || signal.status === "snoozed") && (
          <span className="hidden h-6 w-px shrink-0 bg-gray-200 sm:block" />
        )}

        {/* Lifecycle actions or expand/collapse */}
        {isExpanded ? (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onToggle();
            }}
            className="inline-flex h-7 shrink-0 items-center gap-1 rounded-md border border-gray-200 px-2.5 text-[11px] font-medium text-gray-600 transition-colors hover:bg-gray-100"
          >
            <HiX className="size-3" />
            Collapse
          </button>
        ) : (
          <SignalLifecycleActions variant="inline" {...lifecycleProps} />
        )}
      </div>

      {/* Lifecycle caption for non-active rows (collapsed) */}
      {!isExpanded && !isActive && (
        <div className="border-t border-gray-100 px-4 py-1.5">
          <LifecycleCaption signal={signal} />
        </div>
      )}

      {/* Expanded content */}
      {isExpanded && (
        <div className="border-t border-gray-100">
          {/* Confidence + description + key drivers */}
          <div className="px-4 pt-3">
            {signal.confidence != null && (
              <p
                className={`mb-2 text-sm font-semibold ${
                  CONFIDENCE_COLOR[signal.confidenceLabel] ?? "text-gray-500"
                }`}
              >
                {signal.confidence}% confidence ({signal.confidenceLabel})
              </p>
            )}
            <p className="text-[13px] leading-relaxed text-gray-700">
              {signal.description}
            </p>
            {signal.recurrenceNote && (
              <p className="mt-1 text-[12px] italic leading-relaxed text-gray-500">
                {signal.recurrenceNote}
              </p>
            )}
            {signal.keyDrivers.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1.5">
                {signal.keyDrivers.map((kd, i) => (
                  <span
                    key={i}
                    className="inline-flex items-center rounded-md border border-gray-200 bg-gray-50 px-2 py-0.5 text-[10px] leading-tight text-gray-500"
                  >
                    {kd}
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Underline tabs */}
          <div className="mt-3 flex gap-5 border-b border-gray-200 px-4">
            {tabs.map((tab) => (
              <button
                key={tab.key}
                onClick={() => handleTabChange(tab.key)}
                className={`-mb-px flex items-center gap-1.5 border-b-2 pb-2 text-sm font-semibold transition-colors ${
                  activeTab === tab.key
                    ? "border-gray-900 text-gray-900"
                    : "border-transparent text-gray-400 hover:text-gray-600"
                }`}
              >
                {tab.label}
                <span className="rounded-full bg-gray-100 px-1.5 py-0.5 text-[10px] font-medium text-gray-500">
                  {tab.count}
                </span>
              </button>
            ))}
          </div>

          {/* Tab content */}
          <div className="px-4 py-4">
            {activeTab === "actions" && (
              <div className="space-y-3">
                <div className="flex items-start gap-2 rounded-lg bg-gray-50 px-3 py-2.5">
                  <HiCheckCircle className="mt-0.5 size-4 shrink-0 text-emerald-500" />
                  <p className="text-xs leading-relaxed text-gray-500">
                    Completing actions tracks your progress but does not dismiss
                    or resolve the signal. Use{" "}
                    <span className="font-semibold text-gray-700">Resolve</span>{" "}
                    when the issue is handled.
                  </p>
                </div>
                <div className="space-y-1.5">
                  {signal.actions.map((action) => (
                    <ActionItem
                      key={action.id}
                      action={action}
                      onStatusChange={onStatusChange}
                      onNoteChange={onNoteChange}
                    />
                  ))}
                  {signal.actions.length === 0 && (
                    <p className="text-xs italic text-gray-400">
                      No recommended actions for this signal.
                    </p>
                  )}
                </div>
              </div>
            )}

            {activeTab === "evidence" && (
              <div className="space-y-5">
                {signal.evidence?.sourceEvidence?.length > 0 && (
                  <div>
                    <p className="mb-2 text-[10px] font-semibold uppercase tracking-wider text-gray-400">
                      Source data
                    </p>
                    <div className="space-y-2">
                      {groupEvidenceBySource(
                        signal.evidence.sourceEvidence,
                      ).map(({ type, items }) => {
                        const config =
                          SOURCE_CONFIG[type] ?? SOURCE_CONFIG.usage;
                        const { Icon, color, label } = config;
                        return (
                          <div key={type}>
                            <p className="mb-1.5 flex items-center gap-1 text-[10px] font-medium text-gray-400">
                              <Icon className={`size-3 ${color}`} />
                              {label}
                            </p>
                            <div className="space-y-1.5">
                              {items.map((ev) => (
                                <EvidenceCard key={ev.id} ev={ev} />
                              ))}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {signal.evidence?.metricEvidence?.length > 0 && (
                  <div>
                    <p className="mb-2 text-[10px] font-semibold uppercase tracking-wider text-gray-400">
                      Signals
                    </p>
                    <div className="space-y-2">
                      {groupEvidenceBySource(
                        signal.evidence.metricEvidence,
                      ).map(({ type, items }) => {
                        const config =
                          SOURCE_CONFIG[type] ?? SOURCE_CONFIG.usage;
                        const { Icon, color, label } = config;
                        return (
                          <div key={type}>
                            <p className="mb-1.5 flex items-center gap-1 text-[10px] font-medium text-gray-400">
                              <Icon className={`size-3 ${color}`} />
                              {label}
                            </p>
                            <div className="space-y-1.5">
                              {items.map((ev) => (
                                <EvidenceCard key={ev.id} ev={ev} />
                              ))}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {!signal.evidence?.sourceEvidence?.length &&
                  !signal.evidence?.metricEvidence?.length && (
                    <p className="text-xs italic text-gray-400">
                      Evidence unavailable for this signal.
                    </p>
                  )}
              </div>
            )}
          </div>

          {/* Pinned action bar */}
          <div className="flex items-center justify-between gap-3 border-t border-gray-100 bg-gray-50/60 px-4 py-3">
            {isActive ? (
              <>
                <span className="text-xs text-gray-400">
                  When you&apos;re done with this signal
                </span>
                <SignalLifecycleActions variant="bar" {...lifecycleProps} />
              </>
            ) : (
              <>
                <LifecycleCaption signal={signal} />
                <SignalLifecycleActions variant="bar" {...lifecycleProps} />
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
